import { BaseService, PagedResult, QueryParams } from './base/base.service';

/**
 * Check-In Milestone DTO
 */
export interface CheckInMilestone {
  id: string;
  milestoneName: string;
  dayCount: number;
  tokenAmount: number;
  bonusMultiplier: number | null;
  isActive: boolean;
  description: string | null;
  iconUrl: string | null;
  badgeUrl: string | null;
  sortOrder: number;
  achievementCount?: number;
}

/**
 * Create/Update Milestone Request
 */
export interface MilestoneRequest {
  milestoneName: string;
  dayCount: number;
  tokenAmount: number;
  bonusMultiplier?: number | null;
  isActive: boolean;
  description?: string | null;
  iconUrl?: string | null;
  badgeUrl?: string | null;
  sortOrder?: number;
}

/**
 * Check-In Analytics
 */
export interface CheckInAnalytics {
  todayCheckIns: number;
  activeStreaks: number;
  milestoneAchievements: number;
  averageStreak: number;
  topStreaks: TopStreak[];
  dailyCheckInChart: Array<{
    date: string;
    checkIns: number;
  }>;
}

/**
 * Top Streak User
 */
export interface TopStreak {
  userId: string;
  userName: string;
  userEmail: string;
  userAvatar?: string;
  currentStreak: number;
  longestStreak: number;
  totalCheckIns: number;
  lastCheckInDate: string;
}

/**
 * Check-In History Entry
 */
export interface CheckInHistory {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  checkInDate: string;
  checkInTime: string;
  streakCount: number;
  tokensEarned: number;
  bonusTokens: number;
  isMilestone: boolean;
  milestoneName?: string | null;
}

/**
 * Check-In Service
 * Provides check-in management functionality for admin panel
 */
export class CheckInService extends BaseService {
  constructor() {
    super('/api/wallet');
  }

  /**
   * Get all milestones
   */
  async getMilestones(params: QueryParams = {}): Promise<PagedResult<CheckInMilestone>> {
    const queryParams = new URLSearchParams();
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<CheckInMilestone>>(`/admin/checkin/milestones?${queryParams}`);
    return response.data;
  }

  /**
   * Get milestone by ID
   */
  async getMilestone(id: string): Promise<CheckInMilestone> {
    const response = await this.api.get<CheckInMilestone>(`/admin/checkin/milestones/${id}`);
    return response.data;
  }

  /**
   * Create milestone
   */
  async createMilestone(request: MilestoneRequest): Promise<CheckInMilestone> {
    const response = await this.api.post<CheckInMilestone>('/admin/checkin/milestones', request);
    return response.data;
  }

  /**
   * Update milestone
   */
  async updateMilestone(id: string, request: MilestoneRequest): Promise<CheckInMilestone> {
    const response = await this.api.put<CheckInMilestone>(`/admin/checkin/milestones/${id}`, request);
    return response.data;
  }

  /**
   * Delete milestone
   */
  async deleteMilestone(id: string): Promise<void> {
    await this.api.delete(`/admin/checkin/milestones/${id}`);
  }

  /**
   * Get check-in analytics
   */
  async getAnalytics(dateRange?: { from: string; to: string }): Promise<CheckInAnalytics> {
    const params = new URLSearchParams();
    if (dateRange) {
      params.append('startDate', dateRange.from);
      params.append('endDate', dateRange.to);
    }
    
    const response = await this.api.get<CheckInAnalytics>(`/admin/checkin/analytics?${params}`);
    return response.data;
  }

  /**
   * Get top streaks
   */
  async getTopStreaks(count: number = 100): Promise<TopStreak[]> {
    const response = await this.api.get<TopStreak[]>(`/admin/checkin/top-streaks?count=${count}`);
    return response.data;
  }

  /**
   * Get check-in history
   */
  async getCheckInHistory(params: QueryParams & { userId?: string; startDate?: string; endDate?: string } = {}): Promise<PagedResult<CheckInHistory>> {
    const queryParams = new URLSearchParams();
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.skipCount !== undefined) queryParams.append('skipCount', params.skipCount.toString());
    if (params.maxResultCount !== undefined) queryParams.append('maxResultCount', params.maxResultCount.toString());
    
    const response = await this.api.get<PagedResult<CheckInHistory>>(`/admin/checkin/history?${queryParams}`);
    return response.data;
  }
}

// Export singleton instance
export const checkInService = new CheckInService();
